'use client';
import { useInView } from 'react-intersection-observer';
import { useEffect, useState } from 'react';

import Bath from '../Icons/Bath';
import Bed from '../Icons/Bed';

import {
  FeatureNote,
  VillageFeature,
  VillageFloor,
  VillageRooms,
} from '@/services/types';
import './styles.css';

import useObserverContext from '@/hooks/useObserverContext';
import { IVillageDictionary } from '@/dictionaries/pages/village/types';
import Accordion from '../Accordion';
import RoomsEnum from '../RoomsEnum';
import translateFromArray from '@/utils/get-translated-object';
import { bedTypes } from '@/utils/roomsMap';

type Props = {
  dict: IVillageDictionary;
  villageFeatures: VillageFeature[];
  rooms: VillageRooms[];
  lang: Locale;
};

interface RoomFeature {
  name: string;
  count: number;
  id: number;
}

export default function AmenitiesIndoorContent({
  dict,
  villageFeatures,
  rooms,
  lang,
}: Props) {
  const [isOpen, setIsOpen] = useState(true);
  const { handleCurrentSection } = useObserverContext();
  const { ref, inView } = useInView();
  const { amenities } = dict;

  const villageAccessories: FeatureNote[] = [];

  const accessoriesNotes: FeatureNote[] = [];

  let noteCounter = 1;

  const { bathRoom, bathRooms, bedRoom, bedRooms } = amenities;

  const indoorFeatures = villageFeatures.filter(
    ({ feature }) => feature.type == 'indoor',
  );

  indoorFeatures.forEach(({ feature, translations }) => {
    const translation = translateFromArray({
      lang,
      translations: feature.featureTranslation,
    });

    const hasNotes = translations.length > 0;
    const noteTranslation = translateFromArray({ lang, translations });

    if (hasNotes && noteTranslation?.note) {
      const featureWithNote = {
        description: translation?.title || '',
        noteNumber: noteCounter,
      };

      villageAccessories.push(featureWithNote);

      accessoriesNotes.push({
        description: noteTranslation?.note,
        noteNumber: featureWithNote.noteNumber,
      });
      noteCounter++;
    } else {
      villageAccessories.push({
        description: translation?.title || '',
        noteNumber: undefined,
      });
    }
  });

  function handleFeature(
    featureName: string,
    roomFeatures: RoomFeature[],
    roomId: number,
  ) {
    const startsWithEnum = (str: string) => str.startsWith('enum.');

    if (startsWithEnum(featureName)) {
      const existingFeature = roomFeatures.find(
        (item) => item.name === featureName && item.id === roomId,
      );
      if (!existingFeature) {
        roomFeatures.push({
          name: featureName,
          count: 1,
          id: roomId,
        });
      } else {
        existingFeature.count++;
      }
    }
  }

  function getRoomFeatures(rooms: VillageRooms[]): RoomFeature[] {
    const roomFeatures: RoomFeature[] = [];


    rooms.forEach((room) => {
      if (room.type !== 'other_areas') {
        const roomId = room.id;

        Object.values(room.floors).forEach((floor: VillageFloor) => {
          floor.rooms.forEach((room) => {
            if (room.roomType == 'bathroom') {
              handleFeature('enum.bathroom_type.shower', roomFeatures, roomId);
            }
            if (room.roomType === 'bedroom') {
              Object.values(room.roomFeatures).forEach((feature) => {
                let featureName: string = feature?.[0];

                if (room.title === 'enum.room.double_flexible_bedroom') {
                  if (
                    featureName.includes('bed_type.double') ||
                    featureName.includes('bed_type.twin')
                  ) {
                    return handleFeature(
                      'enum.bed_type.flexible',
                      roomFeatures,
                      roomId,
                    );
                  }
                }

                if (
                  featureName.startsWith('enum.bathroom') &&
                  !featureName.includes('none')
                ) {
                  return handleFeature(
                    'enum.bathroom_type.ensuite_bath_shower',
                    roomFeatures,
                    roomId,
                  );
                }

                handleFeature(featureName, roomFeatures, roomId);
              });
            }
          });
        });
      }
    });
    return roomFeatures;
  }

  useEffect(() => {
    if (inView) {
      handleCurrentSection('amenities');
    } else {
      handleCurrentSection('');
    }
  }, [inView]);

  return (
    <div className="py-[20px] border-b-2 border-gray-200">
      <Accordion
        toggle={() => setIsOpen(!isOpen)}
        isOpen={isOpen}
        label={amenities.indoor}>
        <div className="block h-full transition-all duration-100 ease-in-out">
          <div className="flex flex-wrap">
            {rooms.map(({ floors, translations, id, type }) => {
              const roomFeatures = getRoomFeatures(rooms);
              const roomsEnum = dict.rooms.enums;

              const titleCheck =
                translations.length > 0
                  ? translateFromArray({ lang, translations })?.title
                  : 'Main house';

              const floorTitle =
                type === 'custom' ? titleCheck : roomsEnum[type];

              const onlyBedroomFloors: RoomFeature[] = [];
              const onlyBathroomFloors: RoomFeature[] = [];


              roomFeatures.map((feature) => {
                if (feature.name !== 'enum.bed_type.double' && bedTypes.includes(feature.name) && feature.id === id) {
                  return onlyBedroomFloors.push(feature);
                }
                if (feature.name.includes('bed_type.sofa') && feature.id === id) {
                  return
                }
                if (feature.name.includes('bed_type') && feature.id === id) {
                  const index = onlyBedroomFloors.findIndex(feat => feat.name.includes('enum.bed_type.double'))
                  return index >= 0
                    ? onlyBedroomFloors[index].count += feature.count
                    : onlyBedroomFloors.push({ ...feature, name: 'enum.bed_type.double' });
                }
                if (
                  feature.name.includes('bathroom_type') &&
                  feature.id === id
                ) {
                  onlyBathroomFloors.push(feature);
                }
                if (
                  feature.name === 'enum.room.bathroom' &&
                  feature.id === id
                ) {
                  onlyBathroomFloors.push(feature);
                }
              });

              const bedRoomsTotal = onlyBedroomFloors.reduce((acc, floor) => {
                return acc + floor.count;
              }, 0);

              const bathRoomsTotal = onlyBathroomFloors.reduce((acc, floor) => {
                return acc + floor.count;
              }, 0);

              const canRender = bathRoomsTotal > 0 || bedRoomsTotal > 0;
              return (
                canRender && (
                  <div
                    key={id}
                    className="w-full flex-[0_0_auto] my-8 desktop:w-3/4 desktop::mt-0 desktop:ml-[25%]">
                    <div className="mb-[20px]">
                      <span className="text-body font-semibold">
                        {floorTitle}
                      </span>
                    </div>
                    <div className="flex flex-wrap">
                      {bedRoomsTotal >= 1 && (
                        <div className="w-full desktop:w-1/2 flex-[0_0_auto]">
                          <div className="flex">
                            <Bath className="mr-5" />
                            <div>
                              <p className="mb-4 leading-[1.6]">
                                <span className="font-semibold text-body min-w-6 inline-block text-right mr-[6px]">
                                  {bedRoomsTotal}
                                </span>
                                {bedRoomsTotal > 1 ? bedRooms : bedRoom}
                              </p>
                              <ul className="ml-0 list-none text-sm mb-4">
                                {onlyBedroomFloors.map((bedRoom, index) => {
                                  return (
                                    <RoomsEnum.BedAndBathroomType
                                      key={index}
                                      lang={lang}
                                      enumString={bedRoom.name}
                                      total={bedRoom.count}
                                    />
                                  )
                                })}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                      {bathRoomsTotal >= 1 && (
                        <div className="w-full desktop:w-1/2 flex-[0_0_auto]">
                          <div className="flex">
                            <Bed className="mr-5" />
                            <div>
                              <p className="mb-4 leading-[1.6]">
                                <span className="font-semibold text-body min-w-6 inline-block text-right mr-[6px]">
                                  {bathRoomsTotal}
                                </span>
                                {bathRoomsTotal > 1 ? bathRooms : bathRoom}
                              </p>
                              <ul className="ml-0 list-none text-sm mb-4">
                                {onlyBathroomFloors.map((bathRoom, index) => (
                                  <RoomsEnum.BedAndBathroomType
                                    key={index}
                                    lang={lang}
                                    enumString={bathRoom.name}
                                    total={bathRoom.count}
                                  />
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              );
            })}
          </div>
          <div ref={ref} className="my-8 desktop:mt-0 desktop:ml-[25%]">
            <span className="text-body font-semibold">
              {amenities.accessories}
            </span>
            <div className="desktop-column pt-[32px] columns-1">
              <ul className="ml-0 list-none mb-4 leading-[1.6]">
                {villageAccessories.map((accessory) => (
                  <li className="mb-2" key={accessory.description}>
                    <span>{accessory.description}</span>
                    {accessory.noteNumber && (
                      <sup className="text-xs text-blue ml-2">
                        {accessory.noteNumber}
                      </sup>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8">
              {accessoriesNotes.map((note) => (
                <p key={note.description}>
                  <span className="text-xs text-blue mr-1">
                    {note.noteNumber}
                  </span>
                  <span className="text-xs text-gray-700">
                    {note.description}
                  </span>
                </p>
              ))}
            </div>
          </div>
        </div>
      </Accordion>
    </div>
  );
}
